import { useState } from 'react';
import styled from 'styled-components';
import { useResetGeoLayoutMutation } from '@shared/api';
import {
  findFreeSpot,
  frameRect,
  layoutProblems,
  sheetSize,
  ZONE_DEFAULT_SIZE,
  ZONE_KEYS,
  ZONE_LABELS,
  zoneRect,
  type GeoRect,
  type GeoSettings,
  type GeoZoneKey,
  type GeoZones,
} from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import {
  Check,
  ChoiceCard,
  ChoiceGrid,
  ErrorText,
  FieldGrid,
  FieldLabel,
  Fieldset,
  Hint,
  Muted,
  Notice,
  NumInput,
  Panel,
  PanelHead,
  PanelTitle,
  Stack,
  useAction,
  useDraft,
} from '@widgets/GeoSchemes';
import { StepShell, useSettingsSaver, useStepDirty, useWizard } from './wizard';
import { LayoutEditor } from './LayoutEditor';
import { DraftPreview } from './DraftPreview';

type LayoutDraft = GeoSettings['layout'];

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: ${({ theme }) => theme.spacing(4)};
  align-items: start;

  @container geo-wizard (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const ProblemList = styled.ul`
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing(5)};
`;

function withZone(zones: GeoZones, key: GeoZoneKey, rect: GeoRect | null): GeoZones {
  if (key === 'drawing') return rect ? { ...zones, drawing: rect } : zones;
  return { ...zones, [key]: rect };
}

/** Шаг 5. Области рамки: где чертёж, ситуационный план, обозначения, примечания. */
export function StepLayout() {
  const { scheme, readOnly } = useWizard();
  const s = scheme.settings;
  const layout = useDraft<LayoutDraft>(s.layout);
  useStepDirty('layout', layout.dirty && !readOnly);
  const saver = useSettingsSaver();
  const [resetLayout] = useResetGeoLayoutMutation();
  const resetting = useAction();
  const [selected, setSelected] = useState<GeoZoneKey | null>('drawing');
  const [placeError, setPlaceError] = useState<string | null>(null);

  const size = sheetSize(s.sheet.format, s.sheet.orientation);
  const frame = frameRect(size);
  const d = layout.draft;
  const problems = layoutProblems(d.zones, size);
  const sel = selected ? zoneRect(d.zones, selected) : null;

  function setZone(key: GeoZoneKey, rect: GeoRect | null) {
    layout.update((prev) => ({ ...prev, zones: withZone(prev.zones, key, rect) }));
  }

  function toggleZone(key: GeoZoneKey, on: boolean) {
    setPlaceError(null);
    if (!on) {
      setZone(key, null);
      if (selected === key) setSelected('drawing');
      if (key === 'catalog') layout.update((prev) => ({ ...prev, catalog_mode: 'separate' }));
      return;
    }
    const want = ZONE_DEFAULT_SIZE[key];
    const spot = findFreeSpot(d.zones, size, want);
    // Свободного места нет — ставим в угол рамки: пересечение подсветится, зону подвинут.
    setZone(key, spot ?? { x: frame.x, y: frame.y + frame.h - want.h, w: want.w, h: want.h });
    if (!spot)
      setPlaceError(
        `Для «${ZONE_LABELS[key]}» нет свободного места — подвиньте или уменьшите области.`,
      );
    setSelected(key);
    if (key === 'catalog') layout.update((prev) => ({ ...prev, catalog_mode: 'on_sheet' }));
  }

  function setCatalogMode(mode: LayoutDraft['catalog_mode']) {
    if (mode === 'on_sheet') {
      if (!d.zones.catalog) toggleZone('catalog', true);
      layout.update((prev) => ({ ...prev, catalog_mode: 'on_sheet' }));
    } else {
      layout.update((prev) => ({
        ...prev,
        catalog_mode: mode,
        zones: { ...prev.zones, catalog: null },
      }));
      if (selected === 'catalog') setSelected('drawing');
    }
  }

  async function save(): Promise<boolean> {
    const ok = await saver.save((cur) => ({ ...cur, layout: d }));
    if (ok) layout.reset();
    return ok;
  }

  async function resetToDefault() {
    if (
      layout.dirty &&
      !window.confirm('Сбросить раскладку по умолчанию? Несохранённые правки областей пропадут.')
    )
      return;
    const ok = await resetting.run(
      () => resetLayout(scheme.id).unwrap(),
      'Раскладку сбросить не удалось.',
    );
    if (ok) {
      layout.reset();
      setSelected('drawing');
      setPlaceError(null);
    }
  }

  const blockedReason =
    problems.length > 0
      ? 'Области не должны пересекаться друг с другом и со штампом (ГОСТ Р 51872, прил. В).'
      : null;

  return (
    <StepShell
      n={5}
      lead="Перетаскивайте области мышью или пальцем, тяните за края, чтобы изменить размер — привязка к сетке 5 мм. Штамп формы 3 закреплён в правом нижнем углу. Условные обозначения и примечания — в любом месте листа, но без пересечений."
      save={{
        dirty: layout.dirty && !readOnly,
        busy: saver.busy,
        error: saver.error,
        blockedReason,
        onSave: save,
        onReset: () => {
          layout.reset();
          setPlaceError(null);
        },
      }}
    >
      <Grid>
        <Stack $gap={3}>
          <LayoutEditor
            size={size}
            zones={d.zones}
            problems={problems}
            selected={selected}
            editable={!readOnly}
            onSelect={setSelected}
            onChange={setZone}
          />
          <Muted>
            Лист {s.sheet.format}, {s.sheet.orientation === 'landscape' ? 'альбомный' : 'книжный'} —{' '}
            {size.w}×{size.h} мм. Стрелки — сдвиг области на 5 мм, Shift+стрелки — размер.
          </Muted>
          {problems.length > 0 && (
            <Notice $tone="danger" role="alert">
              <strong>Пересечения — сохранить нельзя:</strong>
              <ProblemList>
                {problems.map((p) => (
                  <li key={p.message}>{p.message}</li>
                ))}
              </ProblemList>
            </Notice>
          )}
          {placeError && <ErrorText role="alert">{placeError}</ErrorText>}
        </Stack>

        <Fieldset disabled={readOnly}>
          <Stack $gap={4}>
            <Panel>
              <PanelHead>
                <PanelTitle>Области листа</PanelTitle>
              </PanelHead>
              <Stack $gap={2}>
                {ZONE_KEYS.map((key) => (
                  <Check
                    key={key}
                    checked={zoneRect(d.zones, key) != null}
                    disabled={
                      key === 'drawing' || (key === 'catalog' && d.catalog_mode !== 'on_sheet')
                    }
                    onChange={(on) => toggleZone(key, on)}
                  >
                    {ZONE_LABELS[key]}
                  </Check>
                ))}
              </Stack>
            </Panel>

            <Panel>
              <PanelHead>
                <PanelTitle>Каталог координат</PanelTitle>
              </PanelHead>
              <ChoiceGrid
                $min={200}
                role="radiogroup"
                aria-label="Где разместить каталог координат"
              >
                <ChoiceCard
                  name="geo-catalog-mode"
                  checked={d.catalog_mode === 'on_sheet'}
                  onSelect={() => setCatalogMode('on_sheet')}
                  title="На листе"
                >
                  в своей области; не влезшее — на листы продолжения
                </ChoiceCard>
                <ChoiceCard
                  name="geo-catalog-mode"
                  checked={d.catalog_mode === 'separate'}
                  onSelect={() => setCatalogMode('separate')}
                  title="Отдельным листом"
                >
                  «Лист 2» с тем же штампом
                </ChoiceCard>
                <ChoiceCard
                  name="geo-catalog-mode"
                  checked={d.catalog_mode === 'none'}
                  onSelect={() => setCatalogMode('none')}
                  title="Без каталога"
                >
                  только если он не требуется
                </ChoiceCard>
              </ChoiceGrid>
            </Panel>

            {sel && selected && (
              <Panel>
                <PanelHead>
                  <PanelTitle>{ZONE_LABELS[selected]}, мм</PanelTitle>
                </PanelHead>
                <FieldGrid $min={110}>
                  <FieldLabel>
                    От левого края
                    <NumInput
                      value={sel.x}
                      onValue={(v) => v != null && setZone(selected, { ...sel, x: v })}
                    />
                  </FieldLabel>
                  <FieldLabel>
                    От низа
                    <NumInput
                      value={sel.y}
                      onValue={(v) => v != null && setZone(selected, { ...sel, y: v })}
                    />
                  </FieldLabel>
                  <FieldLabel>
                    Ширина
                    <NumInput
                      value={sel.w}
                      onValue={(v) => v != null && v > 0 && setZone(selected, { ...sel, w: v })}
                    />
                  </FieldLabel>
                  <FieldLabel>
                    Высота
                    <NumInput
                      value={sel.h}
                      onValue={(v) => v != null && v > 0 && setZone(selected, { ...sel, h: v })}
                    />
                  </FieldLabel>
                </FieldGrid>
              </Panel>
            )}

            <div>
              <Button
                type="button"
                $variant="secondary"
                onClick={() => void resetToDefault()}
                disabled={resetting.busy}
              >
                {resetting.busy ? 'Сбрасываем…' : 'Сбросить по умолчанию'}
              </Button>
              {resetting.error && <ErrorText role="alert">{resetting.error}</ErrorText>}
            </div>
            <Hint>Раскладка по умолчанию рассчитана под текущий формат листа.</Hint>
          </Stack>
        </Fieldset>
      </Grid>

      <DraftPreview beforePreview={layout.dirty ? save : undefined} blockedReason={blockedReason} />
    </StepShell>
  );
}
